import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApiRequest } from '../../utils/apiUtils';
import {
  AvailabilitySlot,
  BusinessCalendarDay,
  CheckoutResponse,
  CreateReservationResult,
  FinalizeResponse,
  ManagedReservationView,
  PublicPrivacyPolicyView,
  PublicReservationFormView,
  ReservationInput,
} from '../../types/reservation';

/** 公開予約ページ（認証不要）の API 群 */

// 予約フォーム初期データ（店舗情報・質問・コース・受付可否・受付範囲など）
export const fetchReservationForm = createAsyncThunk<PublicReservationFormView, string>(
  'publicReservation/fetchReservationForm',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<PublicReservationFormView>(
        `/public/stores/${storeId}/reservation-form`,
        { method: 'GET', requiresAuth: false },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

/**
 * 個人情報の取り扱いの公開本文（type=TEXT の店舗のみ 200・他は 404。フィードバック3）。
 * 専用ページで使い捨てるだけで Redux に保持しないため thunk にはしない。
 */
export const fetchPublicPrivacyPolicy = (storeId: string): Promise<PublicPrivacyPolicyView> =>
  createApiRequest<PublicPrivacyPolicyView>(`/public/stores/${storeId}/privacy-policy`, {
    method: 'GET',
    requiresAuth: false,
  });

// 空き状況（人数選択後は partySize 付きで再取得し「その人数で入れる枠」を正確に表示する）
// menuId を渡すとコースの滞在時間で空き判定・受付時間超過判定を行う（フィードバックB1）。
export const fetchPublicAvailability = createAsyncThunk<
  AvailabilitySlot[],
  { storeId: string; from: string; to: string; partySize?: number; menuId?: string }
>(
  'publicReservation/fetchPublicAvailability',
  async ({ storeId, from, to, partySize, menuId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ from, to });
      if (partySize) params.set('partySize', String(partySize));
      if (menuId) params.set('menuId', menuId);
      return await createApiRequest<AvailabilitySlot[]>(
        `/public/stores/${storeId}/availability?${params.toString()}`,
        { method: 'GET', requiresAuth: false },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 営業カレンダー（月カレンダー表示用）
export const fetchPublicBusinessCalendar = createAsyncThunk<
  BusinessCalendarDay[],
  { storeId: string; from: string; to: string }
>(
  'publicReservation/fetchPublicBusinessCalendar',
  async ({ storeId, from, to }, { rejectWithValue }) => {
    try {
      return await createApiRequest<BusinessCalendarDay[]>(
        `/public/stores/${storeId}/business-calendar?from=${from}&to=${to}`,
        { method: 'GET', requiresAuth: false },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 予約作成（公開・STRICT 検証。サブスク非 entitled は 422）
export const submitPublicReservation = createAsyncThunk<
  CreateReservationResult,
  { storeId: string; input: ReservationInput }
>('publicReservation/submitPublicReservation', async ({ storeId, input }, { rejectWithValue }) => {
  try {
    return await createApiRequest<CreateReservationResult>(
      `/public/stores/${storeId}/reservations`,
      {
        method: 'POST',
        requiresAuth: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

/**
 * 予約デポジット決済の checkout（公開・Phase 2・統合契約 A3）。
 * 予約を PENDING_PAYMENT で作成し PaymentIntent(confirm=false) を作り、
 * clientSecret/stripeAccountId/amount/expiresAt を返す。決済必須店舗のみ呼ぶ
 * （決済不要店舗は従来の submitPublicReservation を使う）。
 * clientSecret は Redux/localStorage に永続せず、Stripe.js への受け渡しだけに使う。
 */
export const checkoutPublicReservation = createAsyncThunk<
  CheckoutResponse,
  { storeId: string; input: ReservationInput }
>(
  'publicReservation/checkoutPublicReservation',
  async ({ storeId, input }, { rejectWithValue }) => {
    try {
      return await createApiRequest<CheckoutResponse>(
        `/public/stores/${storeId}/reservations/checkout`,
        {
          method: 'POST',
          requiresAuth: false,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

/**
 * 予約デポジット決済の finalize（公開・Phase 2・統合契約 A4）。
 * confirmPayment 後（または 3DS リダイレクト復帰後）に呼ぶ。BE が Stripe を真実源として
 * 照会し、確定していれば managementToken/reservationNumber を返す（冪等）。
 * 未完了時は 409（ApiError）か finalized=false を返すため、呼び出し側で数回リトライする。
 */
export const finalizePublicReservation = createAsyncThunk<
  FinalizeResponse,
  { storeId: string; reservationId: string }
>(
  'publicReservation/finalizePublicReservation',
  async ({ storeId, reservationId }, { rejectWithValue }) => {
    try {
      return await createApiRequest<FinalizeResponse>(
        `/public/stores/${storeId}/reservations/${reservationId}/finalize`,
        {
          method: 'POST',
          requiresAuth: false,
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 本人予約照会（予約番号+管理トークン）
export const fetchManagedReservation = createAsyncThunk<
  ManagedReservationView,
  { storeId: string; reservationNumber: string; token: string }
>(
  'publicReservation/fetchManagedReservation',
  async ({ storeId, reservationNumber, token }, { rejectWithValue }) => {
    try {
      return await createApiRequest<ManagedReservationView>(
        `/public/stores/${storeId}/reservations/${encodeURIComponent(
          reservationNumber,
        )}?token=${encodeURIComponent(token)}`,
        { method: 'GET', requiresAuth: false },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// セルフキャンセル（期限超過は 422）
export const cancelManagedReservation = createAsyncThunk<
  void,
  { storeId: string; reservationNumber: string; token: string }
>(
  'publicReservation/cancelManagedReservation',
  async ({ storeId, reservationNumber, token }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/public/stores/${storeId}/reservations/${encodeURIComponent(
          reservationNumber,
        )}?token=${encodeURIComponent(token)}`,
        { method: 'DELETE', requiresAuth: false },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);
